import {
  DataSetDto,
  instanceOfElectronBeamQualityCombinationValueDto,
  instanceOfEnumerationDataSetDto,
  instanceOfNumericEvaluationParameterDto,
  instanceOfPhotonBeamQualityCombinationValueDto,
} from '@/api/generator';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import { getSetupParameterName } from '@/views/tasks/utils';
import { RowData, DataSetSelectionProps } from '@/views/TrendsSelectionView/types';
import { useUnitsStore } from '@/stores/unitsStore';
import { computed } from 'vue';
import { t } from 'i18next';

function getDefinitionParameterSetInfo(forDataSet: DataSetDto): {
  numberOfItems: number;
  displayValues?: string[];
  displayValuesTeaser?: string;
  displayValuesString?: string;
  displayValuesMoreText?: string;
} {
  const unitsStore = useUnitsStore();

  if (instanceOfEnumerationDataSetDto(forDataSet)) {
    return { numberOfItems: 0 };
  }
  if (!forDataSet.evaluationParameter.definitionParameterValueSet) {
    return { numberOfItems: 0 };
  }

  const displayValues = forDataSet.evaluationParameter.definitionParameterValueSet.values
    ?.map((value) => {
      const parameter = forDataSet.evaluationParameter?.definition?.definitionParameters?.find(
        (p) => p.parameterIdentifier === value.parameterIdentifier,
      );
      if (parameter?.name) {
        const unit = unitsStore.getById(parameter.unitId);
        const unitString = unit ? unit.abbreviation : '';
        return `${translateEntityProperty('name', parameter)}: ${toDisplayValue(value.value, {
          fractionDigits: parameter.decimalPlaces,
          unitAbbreviation: unitString,
          valueType: parameter.valueType,
        })}`;
      }
      return value.value.toString();
    })
    .filter(truthy);

  const displayValuesTeaser =
    typeof displayValues?.length === 'number' && displayValues?.length > 1
      ? `${displayValues?.[0]}`
      : undefined;

  const displayValuesString =
    displayValues?.length === 1 ? displayValues[0] : displayValues?.map((v) => `- ${v}`).join('\n');

  const displayValuesMoreText =
    typeof displayValues?.length === 'number' && displayValues?.length > 1
      ? t('And_count_MoreParameter', {
          count: displayValues.length - 1,
        })
      : undefined;

  return {
    numberOfItems: displayValues?.length ?? 0,
    displayValues,
    displayValuesString,
    displayValuesTeaser,
    displayValuesMoreText,
  };
}

function getCellContent(header: any, dataSet: DataSetDto) {
  switch (header.key) {
    case 'evaluation':
      return {
        type: 'datasetHeader',
        evaluationParameterType: instanceOfNumericEvaluationParameterDto(
          dataSet.evaluationParameter,
        )
          ? t('Quantity_one')
          : t('Enumeration_one'),
        evaluationParameter: {
          identifier: dataSet.evaluationParameter.iuid,
          label: getSetupParameterName(dataSet.evaluationParameter),
        },
        evaluationUnitAbbreviation: instanceOfNumericEvaluationParameterDto(
          dataSet.evaluationParameter,
        )
          ? dataSet.evaluationParameter.evaluationUnitAbbreviation
          : undefined,
        evaluationType: instanceOfNumericEvaluationParameterDto(dataSet.evaluationParameter)
          ? t(`EvaluationTypeEnum.${dataSet.evaluationParameter.evaluationType}` as I18nKey)
          : '',
        definition:
          instanceOfNumericEvaluationParameterDto(dataSet.evaluationParameter) &&
          dataSet.evaluationParameter?.definition
            ? getSetupParameterName(dataSet.evaluationParameter.definition)
            : '',
        definitionParameterSetInfo: getDefinitionParameterSetInfo(dataSet),
      };

    case 'medicalSystem':
      return {
        type: 'identifier',
        identifier: dataSet.targetSystem.iuid,
        label: dataSet.targetSystem.name,
      };

    case 'dosimetryEquipment':
      return {
        type: 'supportSystems',
        supportSystems: dataSet.supportSystems,
      };

    case 'job':
      return {
        type: 'identifier',
        identifier: (dataSet as any).jobConfigurationIuid,
        label: dataSet.jobName,
      };

    case 'taskType':
      return {
        type: 'text',
        value: t(`TaskTypeDescriptionEnum.${dataSet.taskType}`),
      };

    case 'numberOfDataPoints':
      return {
        type: 'text',
        value: dataSet.numberOfResults,
      };

    default:
      if (header.key.startsWith('targetParameter_')) {
        const targetSystemParameter = dataSet.targetSystemSetups.find((ts) => ts.id === header.id);
        return { type: 'setupParameter', parameter: targetSystemParameter };
      } else if (header.key.startsWith('supportParameter_')) {
        const supportSystemParameter = dataSet.supportSystemSetups.find(
          (ss) => ss.id === header.id,
        );
        return { type: 'setupParameter', parameter: supportSystemParameter };
      }
      return { type: 'text', value: '-' };
  }
}

function createDataRow(header: any, dataSets: DataSetDto[] | null): RowData {
  const rowData: RowData = {
    headerKey: header.key,
    headerTitle: header.title,
    iuid: header.iuid,
    isIdenticalOverAllSetupValues: null,
    parameterNames: [],
  };

  dataSets?.forEach((dataSet) => {
    const columnKey = `dataset_${dataSet.id}`;
    const cellContent = getCellContent(header, dataSet);
    (rowData as any)[columnKey] = cellContent;

    if (cellContent?.type === 'setupParameter' && cellContent.parameter) {
      if (rowData.isIdenticalOverAllSetupValues === null) {
        rowData.isIdenticalOverAllSetupValues = cellContent.parameter.isIdenticalOverAllSetupValues;
      }
      if (cellContent.parameter.name) {
        rowData.parameterNames.push(cellContent.parameter.name);
      }
    }
  });

  return rowData;
}

export function useDataSetTableModel(
  props: Pick<DataSetSelectionProps, 'dataSets' | 'showDifferences'>,
) {
  const standardHeaders = computed(() => [
    { key: 'evaluation', title: t('EvaluationQuantityOrEnumeration') },
    { key: 'medicalSystem', title: t('MedicalSystem_one') },
    { key: 'dosimetryEquipment', title: t('DosimetryAndQaEquipment') },
    { key: 'job', title: t('Job_one') },
    { key: 'taskType', title: t('TaskType_one') },
    { key: 'numberOfDataPoints', title: t('NumberOfDataPoints') },
  ]);

  const targetSystemParameters = computed(() => {
    if (!props.dataSets) return [];
    const uniqueParameters = new Map();
    props.dataSets.forEach((dataSet) => {
      dataSet.targetSystemSetups.forEach((parameter) => {
        if (!uniqueParameters.has(parameter.id)) {
          uniqueParameters.set(parameter.id, {
            id: parameter.id,
            iuid:
              instanceOfElectronBeamQualityCombinationValueDto(parameter) ||
              instanceOfPhotonBeamQualityCombinationValueDto(parameter)
                ? null
                : parameter.iuid,
            name: getSetupParameterName(parameter),
            key: `targetParameter_${parameter.id}`,
            title: getSetupParameterName(parameter),
          });
        }
      });
    });
    return Array.from(uniqueParameters.values());
  });

  const supportSystemParameters = computed(() => {
    if (!props.dataSets) return [];
    const uniqueParameters = new Map();
    props.dataSets.forEach((dataSet) => {
      dataSet.supportSystemSetups.forEach((parameter) => {
        if (!uniqueParameters.has(parameter.id)) {
          uniqueParameters.set(parameter.id, {
            id: parameter.id,
            iuid:
              instanceOfElectronBeamQualityCombinationValueDto(parameter) ||
              instanceOfPhotonBeamQualityCombinationValueDto(parameter)
                ? null
                : parameter.iuid,
            name: getSetupParameterName(parameter),
            key: `supportParameter_${parameter.id}`,
            title: getSetupParameterName(parameter),
          });
        }
      });
    });
    return Array.from(uniqueParameters.values());
  });

  const tableData = computed<RowData[]>(() => {
    if (!props.dataSets) return [];

    const rows: RowData[] = [];

    standardHeaders.value.forEach((header) => {
      rows.push(createDataRow(header, props.dataSets));
    });

    if (targetSystemParameters.value.length > 0) {
      const targetRows = targetSystemParameters.value.map((header) =>
        createDataRow(header, props.dataSets),
      );
      const hasVisibleTargetRows = targetRows.some(
        (row) =>
          row.isIdenticalOverAllSetupValues === null ||
          !row.isIdenticalOverAllSetupValues ||
          !props.showDifferences,
      );
      if (hasVisibleTargetRows) {
        rows.push({
          headerKey: 'separator_target',
          headerTitle: t('TargetSystemParameter_other'),
          isSeparator: true,
          isIdenticalOverAllSetupValues: null,
          parameterNames: [],
        } as RowData);
        rows.push(...targetRows);
      }
    }

    if (supportSystemParameters.value.length > 0) {
      const supportRows = supportSystemParameters.value.map((header) =>
        createDataRow(header, props.dataSets),
      );
      const hasVisibleSupportRows = supportRows.some(
        (row) =>
          row.isIdenticalOverAllSetupValues === null ||
          !row.isIdenticalOverAllSetupValues ||
          !props.showDifferences,
      );
      if (hasVisibleSupportRows) {
        rows.push({
          headerKey: 'separator_support',
          headerTitle: t('SupportSystemParameter_other'),
          isSeparator: true,
          isIdenticalOverAllSetupValues: null,
          parameterNames: [],
        } as RowData);
        rows.push(...supportRows);
      }
    }

    return rows;
  });

  return { tableData };
}
